import {
  EmptyState,
  ErrorMessage,
  LoadingState,
  Subtitle,
  Title,
  VAlert,
  VButton,
  VStatusIcon,
} from '@vrzl/ui';
import { ArrowLeft, FileQuestion, FileX } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';

import { ApiError } from '@/domains/general/api/client';

import { useDocument } from '../api/queries';
import { indexingStatusIcon, indexingStatusLabels } from '../constants';
import { resolveDocumentKind } from '../lib/documentKind';

import { DocumentDocxPanel } from './documentDocxPanel';
import { DocumentPdfPanel } from './documentPdfPanel';
import { IndexingStatusTooltip } from './indexingStatusTooltip';

export function DocumentViewer() {
  const { eventId, documentId } = useParams();
  const [searchParams] = useSearchParams();

  const caseId = eventId ?? '';
  const docId = documentId ?? '';
  const initialPages = (searchParams.get('pages') ?? '')
    .split(',')
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isFinite(n) && n >= 1);
  const highlightQuery = searchParams.get('q') ?? '';

  const { data: metadata, isPending, error } = useDocument(caseId, docId);
  const kind = resolveDocumentKind(metadata?.sourceFileName);

  const isNotFound = error instanceof ApiError && error.status === 404;

  return (
    <div>
      <div className="mb-4">
        <VButton asChild variant="ghost" size="sm">
          <Link
            to={`/events/${eventId}/documents`}
            className="inline-flex items-center gap-2"
          >
            <ArrowLeft className="size-4 shrink-0" />
            Terug naar documenten
          </Link>
        </VButton>
      </div>

      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Title>{metadata?.name ?? 'Document'}</Title>
          {metadata && (
            <IndexingStatusTooltip
              status={metadata.indexingStatus}
              errors={metadata.indexingErrors}
            >
              <VStatusIcon
                {...indexingStatusIcon[metadata.indexingStatus]}
                label={indexingStatusLabels[metadata.indexingStatus]}
              />
            </IndexingStatusTooltip>
          )}
        </div>
        {metadata?.externalId && (
          <Subtitle className="mt-1">{metadata.externalId}</Subtitle>
        )}
        {metadata?.indexingStatus === 'FAILED_WITH_ERRORS' && (
          <VAlert variant="error" title={indexingStatusLabels.FAILED_WITH_ERRORS} className="mt-3">
            {metadata.indexingErrors ? (
              <pre className="whitespace-pre-wrap text-xs">
                {JSON.stringify(metadata.indexingErrors, null, 2)}
              </pre>
            ) : (
              <p>Geen verdere informatie beschikbaar.</p>
            )}
          </VAlert>
        )}
      </div>

      {isPending && <LoadingState message="Document laden..." />}

      {isNotFound && (
        <EmptyState
          icon={FileX}
          title="Document niet gevonden"
          description="Dit document is niet beschikbaar of bestaat niet."
        />
      )}

      {error && !isNotFound && (
        <ErrorMessage
          error={error}
          message="Het document kon niet worden geladen. Probeer het later opnieuw."
        />
      )}

      {metadata && !error && kind === 'pdf' && (
        <div className="h-[80vh] overflow-hidden rounded-lg border border-border bg-card">
          <DocumentPdfPanel
            eventId={caseId}
            docId={docId}
            initialPages={initialPages}
            highlightQuery={highlightQuery}
            selectedEvidence={null}
          />
        </div>
      )}

      {metadata && !error && kind === 'docx' && (
        <div className="h-[80vh] overflow-hidden rounded-lg border border-border bg-card">
          <DocumentDocxPanel eventId={caseId} docId={docId} selectedEvidence={null} />
        </div>
      )}

      {metadata && !error && kind === 'unknown' && (
        <EmptyState
          icon={FileQuestion}
          title="Documenttype wordt niet ondersteund"
          description="Dit bestand kan niet in de viewer worden weergegeven. Ondersteund zijn PDF- en Word-documenten (.docx)."
        />
      )}
    </div>
  );
}
